// @flow
// Bottom sheets you can pull down with a finger, like the system's: from the grabber strip at the top, or from
// anywhere on the sheet while its list is scrolled to the top. The sheet follows the finger 1:1 and its backdrop
// fades as it goes; on release it closes past a quarter of its height or on a flick, otherwise it settles back.
// Closing calls the same close as the backdrop (Cancelar), and sheetMotion slides it out from where it was let go.
// Touch events (not pointer events): a list that is already scrolling would cancel a pointer stream, and only a
// non-passive touchmove can stop the list from scrolling once the sheet has the gesture.
import { prefersReducedMotion } from './sheetMotion';

export const DRAG_SLOP = 8; // px a finger moves before it's a drag: anything less is a tap
export const GRAB_STRIP = 20; // the strip above the content, around the grabber bar: drags either way
export const GRAB_ZONE = 44; // from the sheet's top edge: a pull down here drags even when the list is scrolled
export const CLOSE_FRACTION = 0.25; // of the sheet's height
export const FLICK_VELOCITY = 0.5; // px/ms
const VELOCITY_WINDOW = 100; // ms of movement the release velocity is measured over
const SETTLE = { duration: 250, easing: 'cubic-bezier(.32, .72, 0, 1)' };
const RUBBER_BAND = 40; // px: how far up a sheet can be pulled, at most (it never gets there). AppLayout.scss's
// .sheetDragging fills 40px of white under the sheet for it: keep them equal.

// Close or settle back? `distance` is how far down the sheet is (px), `velocity` the finger's speed at release
// (px/ms, positive downward).
export const shouldCloseSheet = ({ distance, height, velocity }: { distance: number, height: number, velocity: number }) => {
  if (distance <= 0 || velocity <= -FLICK_VELOCITY) return false; // above its rest, or flicked back up
  return velocity >= FLICK_VELOCITY || distance >= height * CLOSE_FRACTION;
};

// Upward, the sheet resists more and more (iOS's rubber band); downward it follows the finger 1:1
export const dragOffset = (dy: number) => {
  if (dy >= 0) return dy;
  return -(1 - (1 / ((-dy * 0.55) / RUBBER_BAND + 1))) * RUBBER_BAND;
};

// Speed of the last stretch of movement (px/ms), from [time, y] samples
export const releaseVelocity = (samples: Array<[number, number]>) => {
  if (samples.length < 2) return 0;
  const [tEnd, yEnd] = samples[samples.length - 1];
  let first = samples[0];
  for (let i = samples.length - 1; i >= 0; i -= 1) {
    if (tEnd - samples[i][0] > VELOCITY_WINDOW) break;
    first = samples[i];
  }
  const dt = tEnd - first[0];
  return dt > 0 ? (yEnd - first[1]) / dt : 0;
};

// The elements between the target and the sheet that scroll vertically (the sheet itself included)
const scrollersOf = (target: any, sheet: HTMLElement) => {
  const out = [];
  for (let el = target; el && el !== sheet.parentNode; el = el.parentNode) {
    if (el.nodeType === 1 && el.scrollHeight > el.clientHeight + 1) {
      const { overflowY } = window.getComputedStyle(el);
      if (overflowY === 'auto' || overflowY === 'scroll') out.push(el);
    }
  }
  return out;
};

// Where the sheet is drawn now (an entry or a settle may be moving it): the y of its transform
const currentY = (el: HTMLElement) => {
  const t = window.getComputedStyle(el).transform;
  if (!t || t === 'none') return 0;
  const values = t.slice(t.indexOf('(') + 1, -1).split(',').map(Number);
  const y = t.startsWith('matrix3d') ? values[13] : values[5];
  return Number.isFinite(y) ? y : 0;
};

type Options = {
  backdrop?: ?HTMLElement,
  onClose: Function,
};

// Makes `sheet` draggable; returns the function that undoes it
export const attachSheetDrag = (sheet: HTMLElement, { backdrop, onClose }: Options) => {
  let state = 'idle'; // idle → pending (finger down) → dragging
  let start = null;
  let offset = 0;
  let height = 1;
  let samples = [];
  let settling = [];
  let swallow = null; // the click a drag would otherwise end in

  const apply = (y: number) => {
    offset = y;
    sheet.style.transform = `translate3d(0, ${y}px, 0)`;
    if (backdrop) backdrop.style.opacity = String(Math.max(0, 1 - Math.max(0, y) / height));
  };

  const clear = () => {
    sheet.style.transform = '';
    if (backdrop) backdrop.style.opacity = '';
  };

  const stopSettling = () => {
    settling.forEach((a) => { try { a.cancel(); } catch (e) { /* done */ } });
    settling = [];
    sheet.classList.remove('sheetDragging');
  };

  const settleBack = () => {
    const from = offset;
    const opacity = backdrop ? backdrop.style.opacity : '';
    offset = 0;
    clear();
    if (prefersReducedMotion() || typeof sheet.animate !== 'function' || !from) {
      sheet.classList.remove('sheetDragging');
      return;
    }
    // The white under a sheet pulled up stays until it's back at its rest
    settling = [sheet.animate([{ transform: `translate3d(0, ${from}px, 0)` }, { transform: 'translate3d(0, 0, 0)' }], SETTLE)];
    if (backdrop && opacity) settling.push(backdrop.animate([{ opacity }, { opacity: 1 }], SETTLE));
    const done = settling[0].finished;
    if (done) done.then(() => { if (!settling.length || settling[0].playState === 'finished') sheet.classList.remove('sheetDragging'); }, () => {});
    else sheet.classList.remove('sheetDragging');
  };

  // The drag starts where the sheet is drawn: a sheet still entering or settling back is caught there, not
  // snapped to its rest
  const beginDrag = (y: number) => {
    const from = currentY(sheet);
    stopSettling();
    state = 'dragging';
    height = sheet.offsetHeight || 1;
    // An entry still playing would hold the sheet and its backdrop
    sheet.style.animation = 'none';
    if (backdrop) backdrop.style.animation = 'none';
    sheet.classList.add('sheetDragging');
    if (start) start.y = y - from;
    apply(from);
  };

  const onTouchStart = (e: Object) => {
    // A drag whose end never reached the sheet (its row re-rendered away): put it back first
    if (state === 'dragging') settleBack();
    state = 'idle';
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    const fromTop = touch.clientY - sheet.getBoundingClientRect().top;
    start = {
      x: touch.clientX,
      y: touch.clientY,
      target: e.target,
      strip: fromTop < GRAB_STRIP,
      topZone: fromTop < GRAB_ZONE,
      scrollers: scrollersOf(e.target, sheet),
    };
    samples = [[e.timeStamp, touch.clientY]];
    state = 'pending';
  };

  const onTouchMove = (e: Object) => {
    if (state === 'idle' || !start) return;
    const touch = e.touches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const atTop = start.scrollers.every((el) => el.scrollTop <= 0);
    // A pull down that may become a drag: keep the list from taking it while the finger is still in the slop
    const mayPullDown = dy > 0 && (start.topZone || atTop);

    if (state === 'pending') {
      if (Math.abs(dx) < DRAG_SLOP && Math.abs(dy) < DRAG_SLOP) {
        if (e.cancelable && (start.strip || mayPullDown)) e.preventDefault();
        return;
      }
      const vertical = Math.abs(dy) > Math.abs(dx);
      if (!e.cancelable || !vertical || !(start.strip || mayPullDown)) {
        state = 'idle'; // a scroll, a sideways move, or the list already took it
        return;
      }
      // Follow from here, without jumping the slop
      beginDrag(touch.clientY);
    }

    if (e.cancelable) e.preventDefault();
    samples.push([e.timeStamp, touch.clientY]);
    if (samples.length > 20) samples = samples.slice(-20);
    apply(dragOffset(touch.clientY - start.y));
  };

  const onTouchEnd = (e: Object) => {
    if (state !== 'dragging') {
      state = 'idle';
      return;
    }
    state = 'idle';
    swallow = start ? { target: start.target, until: e.timeStamp + 400 } : null;
    // A finger that stopped before lifting is not a flick: the release counts as a sample
    const last = samples[samples.length - 1];
    if (last) samples.push([e.timeStamp, last[1]]);
    const velocity = releaseVelocity(samples);
    if (e.type === 'touchend' && shouldCloseSheet({ distance: offset, height, velocity })) {
      sheet.classList.remove('sheetDragging');
      onClose();
      // Whoever owns the sheet didn't close it: put it back
      window.requestAnimationFrame(() => { if (sheet.isConnected) settleBack(); });
    } else {
      settleBack();
    }
  };

  // The drag ending over the row it started on is not a tap on that row; another tap is
  const onClick = (e: Object) => {
    if (!swallow) return;
    const { target, until } = swallow;
    swallow = null;
    if (e.timeStamp < until && target && (target === e.target || (target.contains && target.contains(e.target)))) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const passive: any = { passive: true };
  const active: any = { passive: false }; // only a touchmove stops the list from scrolling
  sheet.addEventListener('touchstart', onTouchStart, passive);
  sheet.addEventListener('touchmove', onTouchMove, active);
  sheet.addEventListener('touchend', onTouchEnd);
  sheet.addEventListener('touchcancel', onTouchEnd);
  sheet.addEventListener('click', onClick, true);

  return () => {
    stopSettling();
    sheet.removeEventListener('touchstart', onTouchStart, passive);
    sheet.removeEventListener('touchmove', onTouchMove, active);
    sheet.removeEventListener('touchend', onTouchEnd);
    sheet.removeEventListener('touchcancel', onTouchEnd);
    sheet.removeEventListener('click', onClick, true);
  };
};
